import classNames from 'classnames'
import moment from 'moment'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { clearWaitlistActionSuccessMessage, selectWaitlist } from '../../../../reducers/programDashboardReducer'
import { moveEntireWaitlist } from '../../../../actions/waitlistActions'
import { genderLabel } from '../../../../utility/userHelper'

import ProfileAvatar from '../../../common/avatar/ProfileAvatar'
import SuccessModal from '../../../common/customModal/SuccessModal'
import WaitlistActions from '../enrollments-tab/waitlist/WaitlistActions'
import WaitlistProgramSelectorModal from '../enrollments-tab/waitlist/modals/WaitlistProgramSelectorModal'
import WaitlistDetailDrawer from './WaitlistDetailDrawer'

import { ReactComponent as FlagIcon } from '../../../../assets/images/common/icons/Flag.svg'
import { ReactComponent as CirclePlusIcon } from '../../../../assets/images/common/icons/circle-plus-icon.svg'

import classes from './WaitlistTab.module.scss'

const WaitlistTab = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { handleOpenWaitlistStudentsModal } = useOutletContext()
  const program = useSelector(state => state.programDashboard.program)
  const waitlist = useSelector(selectWaitlist)
  const waitlistActionSuccessMessage = useSelector(state => state.programDashboard.waitlistActionSuccessMessage)

  const [isProgramSelectorOpen, setIsProgramSelectorOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [selectedEntry, setSelectedEntry] = useState(null)

  const closeProgramSelector = () => {
    setIsProgramSelectorOpen(false)
    setSelectedCourse(null)
  }

  const handleMoveEntireWaitlist = () => {
    if (waitlist.length > 0) {
      setIsProgramSelectorOpen(true)
    }
  }

  const handleConfirmMove = () => {
    dispatch(moveEntireWaitlist({
      targetCourseId: selectedCourse.id,
      waitlistEntryIds: waitlist.map(entry => entry.id),
      coachMessage: null,
    }))
      .unwrap()
      .catch(() => toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT }))

    closeProgramSelector()
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.stickyAdd}>
        <button
          className={classes.addButton}
          onClick={handleOpenWaitlistStudentsModal}
          aria-label='Add students to waitlist'
        >
          <CirclePlusIcon />
        </button>
      </div>

      {waitlist.length === 0 ? (
        <div className={classNames('d-flex flex-column align-items-center text-center mx-auto', classes.placeholder)}>
          <FlagIcon className={classNames('mb-3', classes.emptyIcon)} width={50} height={50} />
          <h5 className={classes.emptyTitle}>{t('programs.manage.rosterTab.noStudentsYet')}</h5>
          <p className={classes.emptyText}>
            {t('programs.manage.rosterTab.waitlistPlaceholderText')}
          </p>
        </div>
      ) : (
        <>
          <div className={classes.gridTable}>
            {/* Headers */}
            <div className={classNames(classes.col, classes.header, classes.firstColumn)}>
              <span className='font-weight-bold'>{t('programs.manage.waitlistTab.enrollmentsCount', { count: waitlist.length })}</span>
            </div>
            <div className={classNames(classes.col, classes.header, 'pl-5')}>{t('programs.manage.waitlistTab.columns.waitlistStatus')}</div>
            <div className={classNames(classes.col, classes.header)}>{t('programs.manage.waitlistTab.columns.joinedOn')}</div>
            <div className={classNames(classes.col, classes.header)}>{t('common.field.name')}</div>
            <div className={classNames(classes.col, classes.header)}>{t('programs.manage.waitlistTab.columns.age')}</div>
            <div className={classNames(classes.col, classes.header, classes.centeredCol)}>{t('programs.manage.golfersTable.columns.actions')}</div>

            {/* Rows */}
            {waitlist.map((entry, index) => {
              const student = entry.user

              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div className={classes.row} key={entry.id} onClick={() => setSelectedEntry(entry)}>
                  <div className={classNames(classes.col, classes.firstColumn, classes.studentInfo)}>
                    <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                    <div className='d-flex flex-column'>
                      <h6 className='mb-1 font-weight-bold'>{student.fullName}</h6>
                      <span className={classes.studentMeta}>
                        {student.birthdate && `${student.age}yrs`}
                        {student.birthdate && student.gender !== null && '/'}
                        {genderLabel[student.gender]}
                      </span>
                    </div>
                  </div>

                  <div className={classes.col}>
                    <span className={classes.position}>#{index + 1}</span>
                    <span className={classes.waitlistBadge}>{t('programs.manage.waitlistTab.waitlisted')}</span>
                  </div>

                  <div className={classes.col}>
                    <span className={classes.joinedOn}>
                      {entry.createdAt ? `Joined On ${moment(entry.createdAt).format('MM/DD/YYYY')}` : '-'}
                    </span>
                  </div>

                  <div className={classes.col}>{student.fullName}</div>

                  <div className={classes.col}>{student.birthdate ? student.age : '-'}</div>

                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div className={classNames(classes.col, classes.centeredCol)} onClick={e => e.stopPropagation()}>
                    <WaitlistActions waitlistEntry={entry} />
                  </div>
                </div>
              )
            })}
          </div>

          <div className={classes.footer}>
            <button className={classes.moveButton} onClick={handleMoveEntireWaitlist}>
              {t('programs.manage.waitlistTab.moveEntireWaitlist')}
            </button>
          </div>
        </>
      )}

      {isProgramSelectorOpen && (
        <WaitlistProgramSelectorModal
          title='Move Entire Waitlist'
          isOpen={isProgramSelectorOpen}
          currentProgram={program}
          selectedProgram={selectedCourse}
          onSelectProgram={setSelectedCourse}
          students={waitlist.map(entry => entry.user)}
          includeCurrentProgram
          onClose={closeProgramSelector}
          onProceed={handleConfirmMove}
        />
      )}

      {selectedEntry && (
        <WaitlistDetailDrawer
          waitlistEntry={selectedEntry}
          program={program}
          onClose={() => setSelectedEntry(null)}
        />
      )}

      <SuccessModal
        isOpen={!!waitlistActionSuccessMessage}
        onClose={() => dispatch(clearWaitlistActionSuccessMessage())}
        title={waitlistActionSuccessMessage?.title}
        titleClassName={classes.successTitle}
        message={waitlistActionSuccessMessage?.message}
        buttonMessage='Close'
      />
    </div>
  )
}

export default WaitlistTab
